import React, { useState } from 'react';
import { TextField, Button, Container, Typography, Box, Paper, styled } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../logo.png'; // Import the logo image
import AnimatedBrewzLogo from './ui/AnimatedBrewzLogo';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: '#fff',
    backgroundColor: '#333',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: '#444',
    },
    '&.Mui-focused': {
      backgroundColor: '#222',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#555',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#777',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: '#aaa',
    '&.Mui-focused': {
      color: '#06D001',
    },
  },
  // This creates the "notch" effect by giving the shrunken label a background
  '& .MuiInputLabel-shrink': {
    backgroundColor: '#222', // Should match the Paper's background
    padding: '0 4px', // Adds a little space around the label text
  },
}));

function Login() {
  const [email, setEmail] = useState(''); // State for email
  const [password, setPassword] = useState(''); // State for password
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      // Call the updated login function with email and password
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Container component="main" maxWidth="xs" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100vh' }}>
      <Paper 
        elevation={12}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: { xs: 3, sm: 4 },
          backgroundColor: 'black',
          color: '#fff',
          border: '2px solid #rgb(101, 143, 127)',
          borderRadius: 3,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
        }}
      >
        {/* Circular Logo */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            overflow: 'hidden', // Hide overflow to keep image within circle
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <img src={logo} alt="Brewz Coffee Logo" style={{ width: '100%', height: 'auto' }} />
        </Box>
        <AnimatedBrewzLogo />
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {/* Email Input */}
          <StyledTextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {/* Password Input */}
          <StyledTextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <Typography color="error">{error}</Typography>}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              backgroundColor: '#06D001',
              color: '#000',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#9BEC00',
              },
            }}
          >
            Sign In
          </Button>
        </Box>
      </Paper>
      {/* Copyright Information */}
      <Box sx={{ mt: 4, textAlign: 'center', color: '#ffffff' }}>
        <Typography variant="body2">
           All Rights Reserved | © Brewz Coffee
        </Typography>
      </Box>
    </Container>
  );
}

export default Login;